import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Lock, ShieldCheck } from 'lucide-react';
import type { OfferContent } from '../../types/salesPage';
interface OfferVitrineProps {
  content: OfferContent;
  checkoutUrl: string;
}
const MOTES = [{
  left: '34%',
  top: '62%',
  delay: 0
}, {
  left: '64%',
  top: '40%',
  delay: 1.4
}, {
  left: '56%',
  top: '76%',
  delay: 2.8
}, {
  left: '43%',
  top: '26%',
  delay: 4.1
}];
export function OfferVitrine({
  content,
  checkoutUrl
}: OfferVitrineProps) {
  return <section id="oferta" aria-labelledby="oferta-title" className="relative scroll-mt-16 overflow-hidden border-y border-white/[0.07] bg-ink px-5 py-14 sm:px-6 md:py-24" data-id="element-155" data-mp="23ba44pr20cff">
      <div aria-hidden="true" className="grain-overlay absolute inset-0 opacity-60" data-id="element-156" data-mp="1tcueysbl7d8k" />
      <div aria-hidden="true" className="absolute left-1/2 top-0 h-[420px] w-[900px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_top,rgba(210,165,81,0.10),transparent_60%)]" data-id="element-157" data-mp="mwicwg5n9b7p" />

      <div className="relative mx-auto max-w-6xl" data-id="element-158" data-mp="1h55e5uw1c8ep">
        <header className="text-center" data-id="element-159" data-mp="1h3arab17e67z">
          <p className="inline-block border-b border-gold/60 pb-1.5 font-mono text-[9.5px] uppercase tracking-[0.22em] text-gold-soft md:text-[10px] md:tracking-[0.24em]" data-id="element-160" data-mp="1l7iicev4vy9d">
            {content.eyebrow}
          </p>
          <h2 id="oferta-title" className="mt-4 font-serif text-[26px] font-bold leading-tight text-parchment sm:text-[34px] md:text-[44px]" data-id="element-161" data-mp="1c599rujfeeym">
            {content.title}
            <span className="italic text-gold-soft" data-id="element-162" data-mp="128xv4xwd5d10">{content.titleAccent}</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[14.5px] font-light leading-relaxed text-ash md:text-[16px]" data-id="element-163" data-mp="ohspnf6bdqir">
            {content.subtitle}
          </p>
        </header>

        {/* Vitrina: pieza principal sobre pedestal de luz */}
        <div className="relative mt-10 flex justify-center md:mt-16" data-id="element-164" data-mp="ekmyy81m41j2">
          <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-[-40px] h-[520px] w-[520px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(210,165,81,0.20),transparent_62%)]" data-id="element-165" data-mp="jgbtfwyrys8i" />
          <motion.div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-[-50px] h-[420px] w-[380px] -translate-x-1/2 bg-[linear-gradient(180deg,rgba(210,165,81,0.18),rgba(210,165,81,0.02)_70%,transparent)] blur-[2px] [clip-path:polygon(46%_0%,54%_0%,92%_100%,8%_100%)]" animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-166" data-mp="3msmbhsstbcm7" />
          {MOTES.map(mote => <motion.span key={`${mote.left}-${mote.top}`} aria-hidden="true" className="pointer-events-none absolute h-[3px] w-[3px] rounded-full bg-gold-soft shadow-[0_0_6px_rgba(210,165,81,0.9)]" style={{
          left: mote.left,
          top: mote.top
        }} animate={{
          opacity: [0, 0.9, 0.4, 0],
          y: [0, -46]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          delay: mote.delay,
          ease: 'easeInOut'
        }} data-id="element-167" data-mp="9fj9g9e7q9ml" />)}

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="relative flex flex-col items-center" data-id="element-168" data-mp="19jtt5a9ps5cd">
            <span className="relative z-10 bg-ink px-3 font-mono text-[9px] uppercase tracking-[0.22em] text-gold-soft" data-id="element-169" data-mp="1ocvn2s0rbyuc">
              {content.mainItem.tag}
            </span>

            <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="mt-4 overflow-hidden rounded-sm border border-gold/50 bg-[#0F1114] shadow-[0_26px_60px_rgba(0,0,0,0.65),0_0_40px_rgba(200,151,63,0.16)]" data-id="element-170" data-mp="1xc0hbm5ad60p">
              <img src={content.mainItem.image.src} alt={content.mainItem.image.alt} loading="lazy" decoding="async" className="h-[260px] w-[195px] object-cover object-center sm:h-[300px] sm:w-[225px]" data-id="element-171" data-mp="7ydueuegkd3x" />
            </motion.div>

            <div className="mt-5 flex w-[min(100%,340px)] items-center gap-3 rounded-sm border border-white/10 bg-panel px-4 py-3 text-left shadow-[0_10px_24px_rgba(0,0,0,0.4)] sm:w-auto sm:gap-6 sm:px-6" data-id="element-172" data-mp="23d0t7q7nk4bg">
              <div className="min-w-0" data-id="element-173" data-mp="1aolr249mdghj">
                <p className="font-serif text-[15.5px] font-bold text-ivory sm:text-[18px]" data-id="element-174" data-mp="1apn58hlxmj3h">
                  {content.mainItem.name}
                </p>
                <p className="mt-0.5 text-[11.5px] leading-snug text-ash sm:text-[12.5px]" data-id="element-175" data-mp="1jo8ibx8mx5wv">
                  {content.mainItem.description}
                </p>
              </div>
              <span aria-hidden="true" className="h-8 w-px shrink-0 bg-white/10" data-id="element-176" data-mp="rxkj4qqiualu" />
              <div className="shrink-0 text-right" data-id="element-177" data-mp="rvhtw8x70li6">
                <p className="font-mono text-[14px] text-ash line-through" data-id="element-178" data-mp="hsmaxbti2zfe">
                  {content.mainItem.value}
                </p>
                <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-white/35" data-id="element-179" data-mp="mmgri6dt6ya9">
                  Valor
                </p>
              </div>
            </div>

            <div className="relative mt-6 h-[10px] w-[200px] rounded-sm bg-gold shadow-[0_14px_26px_rgba(200,151,63,0.30)] sm:w-[270px]" data-id="element-180" data-mp="cvce3b3s7lhj">
              <span aria-hidden="true" className="absolute left-1/2 top-[10px] h-4 w-[65%] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(ellipse,rgba(0,0,0,0.55),transparent_70%)]" data-id="element-181" data-mp="10ias824fihca" />
            </div>
          </motion.div>
        </div>

        {/* Repisa de bonos */}
        <div className="relative mt-11 md:mt-16" data-id="element-182" data-mp="3bcg8tm33isd2">
          <span aria-hidden="true" className="absolute inset-x-0 top-[132px] hidden h-px bg-[linear-gradient(90deg,transparent,rgba(210,165,81,0.5)_12%,rgba(210,165,81,0.5)_88%,transparent)] lg:block" data-id="element-183" data-mp="3ban4wejvam8r" />
          <ul className="grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 sm:gap-y-8 lg:grid-cols-5 lg:gap-x-6 [&>li:last-child]:col-span-2 sm:[&>li:last-child]:col-span-1" data-id="element-184" data-mp="1hthauwkduxxf">
            {content.bonuses.map((bonus, i) => <motion.li key={bonus.tag} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.07,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-185" data-mp="184fyrhgyl0oq" data-mpi={i}>
                <span className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-white/35" data-id="element-186" data-mp="p520wog3z5sy">
                  {bonus.tag}
                </span>
                <div className="mt-2 overflow-hidden rounded-sm border border-gold/40 bg-[#0F1114] shadow-[0_10px_20px_rgba(0,0,0,0.5)]" data-id="element-187" data-mp="2ohe9zl5v85bs">
                  <img src={bonus.image.src} alt={bonus.image.alt} loading="lazy" decoding="async" className="h-[96px] w-[72px] object-cover object-center" data-id="element-188" data-mp="2r2cap6ztbzk7" />
                </div>
                <h3 className="mt-3 font-serif text-[13.5px] font-bold leading-tight text-ivory sm:text-[14.5px]" data-id="element-189" data-mp="35r8m9e12vupk">
                  {bonus.name}
                </h3>
                <p className="mt-1 font-mono text-[9.5px] text-gold-soft" data-id="element-190" data-mp="285ayb13xix68">{bonus.value} Valor</p>
                <p className="mt-1.5 text-[11.5px] font-light leading-snug text-ash" data-id="element-191" data-mp="2d2td384mo6m5">
                  {bonus.description}
                </p>
              </motion.li>)}
          </ul>
        </div>

        {/* Barra sólida de oferta */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="mt-12 flex flex-col gap-5 rounded-sm border border-white/10 border-t-gold/45 bg-panel px-5 py-6 shadow-[0_18px_40px_rgba(0,0,0,0.45)] sm:px-7 lg:flex-row lg:items-center lg:gap-7 lg:py-5" data-id="element-192" data-mp="3thchax07am4y">
          <div className="shrink-0 text-center lg:text-left" data-id="element-193" data-mp="x5ydr1am0mgy">
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ash" data-id="element-194" data-mp="sd588b7bha3t">
              {content.totalLabel}:{' '}
              <s className="text-[#C98A8A] decoration-[#C98A8A]" data-id="element-195" data-mp="163qrw24lho2r">{content.totalValue}</s>
            </p>
            <p className="mt-1 font-mono text-[12px] uppercase tracking-[0.04em] text-ivory" data-id="element-196" data-mp="122x55c2w2fbg">
              {content.priceLabel}
              <b className="ml-2 font-serif text-[28px] font-extrabold not-italic text-gold-soft" data-id="element-197" data-mp="1g0vjwil4gdrh">
                {content.price}
              </b>
            </p>
          </div>

          <a href={checkoutUrl} rel="noopener" className="group flex flex-1 items-center justify-center gap-2.5 rounded-sm bg-gold px-5 py-4 text-center font-mono text-[10.5px] font-bold uppercase leading-tight tracking-[0.05em] text-[#181205] shadow-[0_16px_34px_rgba(200,151,63,0.28)] transition-colors duration-200 hover:bg-gold-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-soft focus-visible:ring-offset-2 focus-visible:ring-offset-panel sm:text-[12px] md:px-6" data-id="element-198" data-mp="3huurasv4iayw">
            {content.ctaLabel}
            <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1" data-id="element-199" data-mp="19ly0vb7g74o9" />
          </a>

          <ul className="flex shrink-0 flex-col gap-2 font-mono text-[9.5px] uppercase tracking-[0.08em] text-ash" data-id="element-200" data-mp="zplbgpixrczb">
            <li className="flex items-center justify-center gap-2 lg:justify-end" data-id="element-201" data-mp="14oo3989wnmpc">
              <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 text-gold/80" data-id="element-202" data-mp="2u2kw4x26axdg" />
              {content.microcopy[0]}
            </li>
            <li className="flex items-center justify-center gap-2 lg:justify-end" data-id="element-203" data-mp="2z14gwu516348">
              <Lock aria-hidden="true" className="h-3.5 w-3.5 text-gold/80" data-id="element-204" data-mp="3vrvu5jl34fqz" />
              {content.microcopy[1]}
            </li>
          </ul>
        </motion.div>
      </div>
    </section>;
}